import React, { useRef, useState, Fragment } from 'react';
import { Upload, Plus, Trash2, Check, ChevronsUpDown } from 'lucide-react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { EventSchema, UnifiedSchema } from '../../types/schema';
import { Integration } from '../../types/integration';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';

interface EventListPanelProps {
  unifiedSchema: UnifiedSchema | null;
  selectedEvent: EventSchema | null;
  setSelectedEvent: (event: EventSchema | null) => void;
  isEditable: boolean;
  handleDuplicateEvent: (event: EventSchema) => void;
  handleDeleteEvent: (event: EventSchema) => void;
  handleAddEmptyEvent: () => void;
  processFiles: (files: File[]) => Promise<void>;
  isDragOver: boolean;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
  isMobile: boolean;
  isTablet: boolean;
  isDesktopMedium: boolean;
  integrations: Integration[]; // New prop
  selectedIntegrationId: string | null; // New prop
  onIntegrationChange: (integrationId: string | null) => void; // New prop
}

const EventListPanel: React.FC<EventListPanelProps> = ({
  unifiedSchema,
  selectedEvent,
  setSelectedEvent,
  isEditable,
  handleDeleteEvent,
  handleAddEmptyEvent,
  processFiles,
  isDragOver,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  isMobile,
  integrations,
  selectedIntegrationId,
  onIntegrationChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [integrationSearchTerm, setIntegrationSearchTerm] = useState('');

  const panelWidthClass = "w-[480px]";

  const filteredIntegrations = integrations.filter(integration =>
    integration.name.toLowerCase().includes(integrationSearchTerm.toLowerCase())
  );

  const selectedIntegration = selectedIntegrationId
    ? integrations.find(integration => integration.id === selectedIntegrationId)
    : null;

  return (
    <div className={clsx(panelWidthClass, "bg-white dark:bg-gray-800 border-r dark:border-gray-700 p-4 md:p-6 flex flex-col gap-4 shadow-sm min-h-0")}>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".json"
        multiple
        onChange={async (e) => {
          const files = e.target.files;
          if (files && files.length > 0) {
            await processFiles(Array.from(files));
          }
        }}
      />

      {unifiedSchema?.type === 'vendor_schema' && (
        <div className="w-full">
          <label htmlFor="integration-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Integration
          </label>
          <Listbox
            value={selectedIntegrationId}
            onChange={(id) => onIntegrationChange(id)}
            disabled={!isEditable}
          >
            {({ open }) => (
              <div className="relative mt-1">
                <ListboxButton className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                  <span className="flex items-center">
                    {selectedIntegration?.logo_url && (
                      <img src={selectedIntegration.logo_url} alt={selectedIntegration.name} className="h-5 w-5 rounded-full mr-2" />
                    )}
                    <span className="block truncate">
                      {selectedIntegration ? selectedIntegration.name : 'Select an integration'}
                    </span>
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronsUpDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </span>
                </ListboxButton>
                <Transition
                  show={open}
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <ListboxOptions className="absolute z-10 max-h-60 w-full overflow-auto rounded-md bg-white pb-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm dark:bg-gray-800 dark:ring-gray-700">
                    <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 p-2 border-b dark:border-gray-700">
                      <input
                        type="text"
                        placeholder="Search integrations..."
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={integrationSearchTerm}
                        onChange={(e) => setIntegrationSearchTerm(e.target.value)}
                      />
                    </div>
                    <ListboxOption
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${
                          active ? 'bg-blue-600 text-white' : 'text-gray-900 dark:text-white'
                        }`
                      }
                      value={null} // Option to unassign
                    >
                      {({ selected, active }) => (
                        <>
                          <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                            None
                          </span>
                          {selected ? (
                            <span
                              className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                active ? 'text-white' : 'text-blue-600'
                              }`}
                            >
                              <Check className="h-5 w-5" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </ListboxOption>
                    {filteredIntegrations.map((integration) => (
                      <ListboxOption
                        key={integration.id}
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-10 pr-4 ${
                            active ? 'bg-blue-600 text-white' : 'text-gray-900 dark:text-white'
                          }`
                        }
                        value={integration.id}
                      >
                        {({ selected, active }) => (
                          <>
                            <span className="flex items-center">
                              {integration.logo_url && (
                                <img src={integration.logo_url} alt={integration.name} className="h-5 w-5 rounded-full mr-2" />
                              )}
                              <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                {integration.name}
                              </span>
                            </span>
                            {selected ? (
                              <span
                                className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                  active ? 'text-white' : 'text-blue-600'
                                }`}
                              >
                                <Check className="h-5 w-5" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </Transition>
              </div>
            )}
          </Listbox>
        </div>
      )}

      <div className="flex items-center justify-between">
        {!isMobile && (
          <div className="text-text-primary dark:text-white font-bold text-base md:text-lg">Events</div>
        )}
        {unifiedSchema && unifiedSchema.events.length > 0 && (
          <div className="flex gap-1">
            <button
              className={clsx(
                "flex items-center gap-2 px-4 py-2 text-blue-600 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md text-sm font-medium transition-colors",
                !isEditable && 'opacity-50 cursor-not-allowed'
              )}
              onClick={() => fileInputRef.current?.click()}
              disabled={!isEditable}
            >
              <Upload size={16} className="text-blue-700 dark:text-white" />
              Upload
            </button>
            <button
              className={clsx(
                "flex items-center gap-2 px-4 py-2 border border-blue-300 bg-blue-100  dark:bg-blue-900/30 dark:border-blue-700/50 hover:bg-blue-200 dark:hover:bg-blue-900  shadow hover:shadow-md text-blue-800 dark:text-blue-100 rounded-md text-sm font-medium transition-colors",
                !isEditable && 'opacity-50 cursor-not-allowed'
              )}
              onClick={handleAddEmptyEvent}
              disabled={!isEditable}
            >
              <Plus size={16} className="text-blue-700 dark:text-white" />
              {!isMobile && "Add "}Event
            </button>
          </div>
        )}
      </div>

      {(!unifiedSchema || unifiedSchema.events.length === 0) ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`flex flex-col items-center justify-center py-12 px-6 md:px-10 border-2 border-dashed rounded-lg bg-gray-50 dark:bg-gray-700 ${isDragOver ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : 'border-gray-300 dark:border-gray-600'}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Upload size={48} className="text-gray-400 dark:text-gray-500 mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">No events added yet</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-2">
            Upload JSON event schemas or add an empty event.
          </p>
          <div className="flex gap-2 mt-4">
            <button
              className={clsx(
                "flex items-center gap-2 px-4 py-2 border border-blue-300 bg-blue-100  dark:bg-blue-900/30 dark:border-blue-700/50 hover:bg-blue-200 dark:hover:bg-blue-900  shadow hover:shadow-md text-blue-800 dark:text-blue-100 rounded-md text-sm font-medium transition-colors",
                !isEditable && 'opacity-50 cursor-not-allowed'
              )}
              onClick={() => fileInputRef.current?.click()}
              disabled={!isEditable}
            >
              <Upload size={16} className="text-blue-700 dark:text-white" />
              Upload
            </button>
            <button
              className={clsx(
                "flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg text-white rounded-md text-sm font-medium transition-colors",
                !isEditable && 'opacity-50 cursor-not-allowed'
              )}
              onClick={handleAddEmptyEvent}
              disabled={!isEditable}
            >
              <Plus size={16} className="text-white" />
              {!isMobile && "Add "}Event
            </button>
          </div>
        </motion.div>
      ) : (
        <div
          className={`flex-1 overflow-y-auto border rounded-md ${isDragOver ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : 'border-gray-200 dark:border-gray-700'}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {unifiedSchema && unifiedSchema.events.map((event: EventSchema) => (
            <div
              key={event.id}
              className={clsx(
                `flex items-center justify-between px-5 py-4 cursor-pointer border-b dark:border-gray-700 group`,
                selectedEvent?.id === event.id ? 'bg-blue-100/60 dark:bg-blue-700/20 hover:dark:bg-blue-700/30' : 'hover:bg-gray-50 dark:hover:bg-gray-600/20'
              )}
              onClick={() => setSelectedEvent(event)}
            >
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <div className="font-medium text-gray-900 text-[15px] dark:text-white truncate">{event.name || 'Unnamed Event'}</div>
                {!isMobile && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono font-medium bg-blue-100 dark:bg-blue-500/10 text-blue-800 dark:text-blue-300 truncate">
                    {event.codeName || 'no_code_name'}
                  </span>
                )}
              </div>
              <button
                className="p-1 rounded-full hover:bg-red-100 dark:hover:bg-red-900 text-red-500 dark:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteEvent(event);
                }}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventListPanel;
